import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Grid,
  Autocomplete,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Add, Delete, CheckCircle, Cancel } from "@mui/icons-material";
import { Button, Card, LoadingSpinner, ErrorMessage, AllergenChip, DecimalTextField } from "@/components/ui";
import {
  productService,
  CreateProductData,
  ProductIngredientInput,
  DishCategory,
  Weekday,
} from "@/services/product.service";
import { categoryService } from "@/services/category.service";
import { ingredientService } from "@/services/ingredient.service";
import { useToastStore } from "@/store/toastStore";
import { MEASUREMENT_UNITS, getUnitShort } from "@/constants/measurementUnits";
import { parseDecimal } from "@/utils/decimalInput";
import type { Ingredient, IngredientCategory } from "@/types";

const INGREDIENT_CATEGORY_LABELS: Record<IngredientCategory, string> = {
  MEAT: "Viande",
  FISH: "Poisson",
  VEGETABLE: "Légume",
  STARCH: "Féculent",
  DAIRY: "Produit laitier",
  SPICE: "Épice",
  FRUIT: "Fruit",
  OTHER: "Autre",
};

const DISH_CATEGORY_LABELS: Record<DishCategory, string> = {
  FISH: "Poisson",
  GRILLED_MEAT: "Grillade",
  VEGETARIAN: "Végétarien",
  TRADITIONAL: "Traditionnel",
  EXOTIC: "Exotique",
  STEW: "Ragoûts",
  OTHER: "Autre",
};

const WEEKDAY_OPTIONS: Array<{ code: Weekday; label: string }> = [
  { code: "MONDAY", label: "Lundi" },
  { code: "TUESDAY", label: "Mardi" },
  { code: "WEDNESDAY", label: "Mercredi" },
  { code: "THURSDAY", label: "Jeudi" },
  { code: "FRIDAY", label: "Vendredi" },
  { code: "SATURDAY", label: "Samedi" },
  { code: "SUNDAY", label: "Dimanche" },
];

// Schéma de validation Zod
const productSchema = z.object({
  name: z
    .string()
    .min(1, "Le nom est obligatoire")
    .min(2, "Le nom doit contenir au moins 2 caractères"),
  description: z.string().optional(),
  categoryId: z.string().min(1, "La catégorie est obligatoire"),
  price: z
    .number()
    .min(0, "Le prix doit être supérieur ou égal à 0")
    .max(10000, "Le prix ne peut pas dépasser 10000€"),
  type: z.enum(["STARTER", "MAIN_COURSE", "DESSERT", "DRINK", "SIDE"], {
    required_error: "Le type de produit est obligatoire",
  }),
  dishCategory: z.enum(["FISH", "GRILLED_MEAT", "VEGETARIAN", "TRADITIONAL", "EXOTIC", "STEW", "OTHER"]).optional().nullable(),
  isVegetarian: z.boolean().default(false),
  isRecurrent: z.boolean().default(false),
  recurrentDays: z.array(z.enum(["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"])).default([]),
  isPremium: z.boolean().default(false),
  isSpicy: z.boolean().default(false),
  isSwitch: z.boolean().default(false),
  isAvailable: z.boolean().default(true),
  displayOrder: z.number().int().min(0).default(0),
}).superRefine((data, ctx) => {
  if (data.isRecurrent && data.recurrentDays.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["recurrentDays"],
      message: "Sélectionnez au moins un jour pour un produit récurrent",
    });
  }
});

type ProductFormData = z.infer<typeof productSchema>;

/**
 * Page de formulaire pour créer ou éditer un produit
 */
export function ProductFormPage(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  // Récupérer les catégories
  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: () => categoryService.getAll(),
  });

  // Récupérer le produit si édition
  const {
    data: product,
    isLoading: productLoading,
    error: productError,
  } = useQuery({
    queryKey: ["products", id],
    queryFn: () => productService.getById(id!),
    enabled: isEditing,
  });

  // Liste des ingrédients (pour la composition)
  const { data: ingredientsList = [] } = useQuery({
    queryKey: ["ingredients"],
    queryFn: () => ingredientService.getAll(),
  });

  // Composition du produit (ingrédients + quantité/unité)
  const [productIngredients, setProductIngredients] = useState<ProductIngredientInput[]>([]);
  const [addIngredient, setAddIngredient] = useState<Ingredient | null>(null);
  const [addQuantity, setAddQuantity] = useState<string>("");
  const [addUnit, setAddUnit] = useState<string>("");

  // Initialiser la composition depuis le produit chargé (édition)
  useEffect(() => {
    if (product?.ingredients && product.ingredients.length > 0) {
      setProductIngredients(
        product.ingredients.map((pi, index) => ({
          ingredientId: pi.ingredientId,
          quantity: pi.quantity != null ? Number(pi.quantity) : undefined,
          unit: pi.unit ?? undefined,
          isPrimary: pi.isPrimary,
          displayOrder: index,
        })),
      );
    } else if (product && !product.ingredients?.length) {
      setProductIngredients([]);
    }
  }, [product?.id]);

  // Formulaire
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      name: "",
      description: "",
      categoryId: "",
      price: 0,
      type: "MAIN_COURSE",
      dishCategory: null,
      isVegetarian: false,
      isRecurrent: false,
      recurrentDays: [],
      isPremium: false,
      isSpicy: false,
      isSwitch: false,
      isAvailable: true,
      displayOrder: 0,
    },
  });

  // Remplir le formulaire avec les données du produit si édition
  useEffect(() => {
    if (product) {
      const price =
        typeof product.price === "string"
          ? parseFloat(product.price)
          : product.price;
      reset({
        name: product.name,
        description: product.description || "",
        categoryId: product.categoryId,
        price,
        type: product.type,
        dishCategory: product.dishCategory ?? null,
        isVegetarian: product.isVegetarian,
        isRecurrent: product.isRecurrent ?? false,
        recurrentDays: product.recurrentDays ?? [],
        isPremium: product.isPremium ?? false,
        isSpicy: product.isSpicy ?? false,
        isSwitch: product.isSwitch ?? false,
        isAvailable: product.isAvailable,
        displayOrder: product.displayOrder,
      });
    }
  }, [product, reset]);

  // Store pour les toasts
  const { success, error: showError } = useToastStore();

  // Mutation pour créer
  const createMutation = useMutation({
    mutationFn: (data: CreateProductData) => productService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["products", "recurrent"] });
      queryClient.invalidateQueries({ queryKey: ["products", "all"] });
      success("Produit créé avec succès");
      navigate("/products");
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message || "Erreur lors de la création du produit",
      );
    },
  });

  // Mutation pour mettre à jour
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateProductData>;
    }) => productService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["products", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["products", "recurrent"] });
      queryClient.invalidateQueries({ queryKey: ["products", "all"] });
      success("Produit modifié avec succès");
      navigate("/products");
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message ||
          "Erreur lors de la modification du produit",
      );
    },
  });

  const onSubmit = (data: ProductFormData) => {
    const productData: CreateProductData = {
      name: data.name,
      description: data.description || undefined,
      categoryId: data.categoryId,
      price: data.price,
      type: data.type,
      dishCategory: data.dishCategory || undefined,
      isVegetarian: data.isVegetarian ?? false,
      isRecurrent: data.isRecurrent ?? false,
      recurrentDays: data.isRecurrent ? data.recurrentDays : [],
      isPremium: data.isPremium ?? false,
      isSpicy: data.isSpicy ?? false,
      isSwitch: data.isSwitch ?? false,
      isAvailable: data.isAvailable ?? true,
      displayOrder: data.displayOrder,
      ingredients: productIngredients.map((pi, index) => ({
        ingredientId: pi.ingredientId,
        quantity: pi.quantity,
        unit: pi.unit,
        isPrimary: pi.isPrimary ?? true,
        displayOrder: index,
      })),
    };

    if (isEditing) {
      updateMutation.mutate({ id: id!, data: productData });
    } else {
      createMutation.mutate(productData);
    }
  };

  const handleAddIngredient = () => {
    if (!addIngredient) return;
    if (productIngredients.some((pi) => pi.ingredientId === addIngredient.id)) return;
    const q = addQuantity.trim() ? parseDecimal(addQuantity) : undefined;
    setProductIngredients((prev) => [
      ...prev,
      {
        ingredientId: addIngredient.id,
        quantity: q != null && !Number.isNaN(q) ? q : undefined,
        unit: addUnit.trim() || undefined,
        isPrimary: true,
        displayOrder: prev.length,
      },
    ]);
    if (!addIngredient.isVegetarian) {
      setValue("isVegetarian", false);
    }
    setAddIngredient(null);
    setAddQuantity("");
    setAddUnit("");
  };

  const handleRemoveIngredient = (ingredientId: string) => {
    setProductIngredients((prev) =>
      prev.filter((pi) => pi.ingredientId !== ingredientId).map((pi, index) => ({ ...pi, displayOrder: index })),
    );
  };

  const getIngredientName = (ingredientId: string) =>
    ingredientsList.find((i) => i.id === ingredientId)?.name ?? ingredientId;

  const getIngredient = (ingredientId: string): Ingredient | undefined =>
    ingredientsList.find((i) => i.id === ingredientId);

  const isLoading = categoriesLoading || (isEditing && productLoading);
  const mutationError = createMutation.error || updateMutation.error;

  if (isLoading) {
    return (
      <LoadingSpinner
        message={isEditing ? "Chargement du produit..." : "Chargement..."}
      />
    );
  }

  if (isEditing && productError) {
    return <ErrorMessage message="Erreur lors du chargement du produit." />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Titre */}
      <Typography variant="h2" sx={{ fontSize: "24px", fontWeight: 700 }}>
        {isEditing ? "Modifier le produit" : "Nouveau produit"}
      </Typography>

      {/* Formulaire */}
      <Card>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          {mutationError && (
            <ErrorMessage
              message={
                (mutationError as any)?.response?.data?.message ||
                "Une erreur est survenue lors de la sauvegarde."
              }
            />
          )}

          <Grid container spacing={3}>
            {/* Nom */}
            <Grid item xs={12} md={6}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nom du produit"
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    required
                  />
                )}
              />
            </Grid>

            {/* Catégorie */}
            <Grid item xs={12} md={6}>
              <Controller
                name="categoryId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.categoryId} required>
                    <InputLabel>Catégorie</InputLabel>
                    <Select
                      {...field}
                      label="Catégorie"
                      disabled={categories.length === 0}
                    >
                      {categories.length === 0 ? (
                        <MenuItem disabled>
                          Aucune catégorie disponible
                        </MenuItem>
                      ) : (
                        categories
                          .filter((cat) => cat.isActive)
                          .map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                              {category.name}
                            </MenuItem>
                          ))
                      )}
                    </Select>
                    {errors.categoryId && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 1.75 }}
                      >
                        {errors.categoryId.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description"
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Description détaillée du produit..."
                  />
                )}
              />
            </Grid>

            {/* Prix et Type */}
            <Grid item xs={12} md={6}>
              <Controller
                name="price"
                control={control}
                render={({ field }) => (
                  <DecimalTextField
                    {...field}
                    label="Prix (€)"
                    fullWidth
                    value={field.value}
                    onChange={field.onChange}
                    inputProps={{ min: 0, max: 10000, step: 0.01 }}
                    error={!!errors.price}
                    helperText={errors.price?.message}
                    required
                    InputProps={{
                      endAdornment: "€",
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.type} required>
                    <InputLabel>Type de produit</InputLabel>
                    <Select {...field} label="Type de produit">
                      <MenuItem value="STARTER">Entrée</MenuItem>
                      <MenuItem value="MAIN_COURSE">Plat principal</MenuItem>
                      <MenuItem value="DESSERT">Dessert</MenuItem>
                      <MenuItem value="DRINK">Boisson</MenuItem>
                      <MenuItem value="SIDE">Accompagnement / 100 % Légumes</MenuItem>
                    </Select>
                    {errors.type && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 1.75 }}
                      >
                        {errors.type.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Catégorie de plat (si MAIN_COURSE) */}
            {watch("type") === "MAIN_COURSE" && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="dishCategory"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Catégorie de plat</InputLabel>
                      <Select 
                        {...field} 
                        label="Catégorie de plat"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value || null)}
                      >
                        <MenuItem value="">
                          <em>Aucune</em>
                        </MenuItem>
                        {(Object.entries(DISH_CATEGORY_LABELS) as [DishCategory, string][]).map(([cat, label]) => (
                          <MenuItem key={cat} value={cat}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            )}

            {/* Ordre d'affichage */}
            <Grid item xs={12} md={6}>
              <Controller
                name="displayOrder"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Ordre d'affichage"
                    fullWidth
                    type="number"
                    inputProps={{ min: 0 }}
                    error={!!errors.displayOrder}
                    helperText={
                      errors.displayOrder?.message ||
                      "Détermine l'ordre d'affichage dans les listes"
                    }
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value, 10) || 0)
                    }
                  />
                )}
              />
            </Grid>

            {/* Options */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Controller
                  name="isVegetarian"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Produit végétarien"
                    />
                  )}
                />
                <Controller
                  name="isRecurrent"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(_, checked) => {
                            field.onChange(checked);
                            if (!checked) {
                              setValue("recurrentDays", []);
                            }
                          }}
                        />
                      }
                      label="Produit récurrent (Bowls, tartes salées, soupes, etc.)"
                    />
                  )}
                />
                {watch("isRecurrent") && (
                  <Controller
                    name="recurrentDays"
                    control={control}
                    render={({ field }) => (
                      <Box sx={{ pl: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          Jours de récurrence
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {WEEKDAY_OPTIONS.map((day) => {
                            const checked = field.value?.includes(day.code) ?? false;
                            return (
                              <FormControlLabel
                                key={day.code}
                                control={
                                  <Checkbox
                                    checked={checked}
                                    onChange={(_, isChecked) => {
                                      const current = field.value ?? [];
                                      if (isChecked) {
                                        field.onChange([...current, day.code]);
                                        return;
                                      }
                                      field.onChange(current.filter((d) => d !== day.code));
                                    }}
                                  />
                                }
                                label={day.label}
                              />
                            );
                          })}
                        </Box>
                        {errors.recurrentDays && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                            {errors.recurrentDays.message as string}
                          </Typography>
                        )}
                      </Box>
                    )}
                  />
                )}
                <Controller
                  name="isPremium"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Produit Premium (plats et desserts haut de gamme)"
                    />
                  )}
                />
                <Controller
                  name="isSpicy"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Épicé"
                    />
                  )}
                />
                <Controller
                  name="isAvailable"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Produit disponible à la vente"
                    />
                  )}
                />
              </Box>
            </Grid>

            {/* Composition du produit (ingrédients) */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Composition du produit
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Ajoutez les ingrédients qui composent ce produit (optionnel). Quantité et unité sont facultatifs.
              </Typography>
              {productIngredients.length > 0 && (
                <TableContainer component={Box} sx={{ mb: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Ingrédient</TableCell>
                        <TableCell>Catégorie</TableCell>
                        <TableCell>Allergènes</TableCell>
                        <TableCell align="center">Végétarien</TableCell>
                        <TableCell align="center">Vegan</TableCell>
                        <TableCell align="right">Quantité</TableCell>
                        <TableCell>Unité</TableCell>
                        <TableCell align="right" width={60} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productIngredients.map((pi) => {
                        const ing = getIngredient(pi.ingredientId);
                        return (
                          <TableRow key={pi.ingredientId}>
                            <TableCell>{getIngredientName(pi.ingredientId)}</TableCell>
                            <TableCell>
                              {ing ? INGREDIENT_CATEGORY_LABELS[ing.category] ?? ing.category : "—"}
                            </TableCell>
                            <TableCell>
                              {ing?.allergens?.length ? (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {ing.allergens.map((ia) => (
                                    <AllergenChip
                                      key={ia.allergen.id}
                                      label={
                                        ia.allergen.icon
                                          ? `${ia.allergen.icon} ${ia.allergen.code}`
                                          : ia.allergen.code
                                      }
                                      compact
                                    />
                                  ))}
                                </Box>
                              ) : (
                                "—"
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {ing ? (
                                ing.isVegetarian ? (
                                  <CheckCircle sx={{ color: "success.main", fontSize: 20 }} />
                                ) : (
                                  <Cancel sx={{ color: "text.disabled", fontSize: 20 }} />
                                )
                              ) : (
                                "—"
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {ing ? (
                                ing.isVegan ? (
                                  <CheckCircle sx={{ color: "success.main", fontSize: 20 }} />
                                ) : (
                                  <Cancel sx={{ color: "text.disabled", fontSize: 20 }} />
                                )
                              ) : (
                                "—"
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {pi.quantity != null ? pi.quantity : "—"}
                            </TableCell>
                            <TableCell>{getUnitShort(pi.unit)}</TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveIngredient(pi.ingredientId)}
                                aria-label="Retirer l'ingrédient"
                                color="error"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "flex-start" }}>
                <Autocomplete
                  value={addIngredient}
                  onChange={(_, v) => setAddIngredient(v)}
                  options={ingredientsList.filter(
                    (i) => !productIngredients.some((pi) => pi.ingredientId === i.id),
                  )}
                  getOptionLabel={(opt) => opt.name}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Ingrédient" size="small" placeholder="Choisir un ingrédient" />
                  )}
                  sx={{ minWidth: 220 }}
                />
                <TextField
                  size="small"
                  label="Quantité"
                  type="text"
                  inputMode="decimal"
                  value={addQuantity}
                  onChange={(e) =>
                    setAddQuantity(e.target.value.replace(".", ","))
                  }
                  onKeyDown={(e) => {
                    if (e.key !== ".") return;
                    e.preventDefault();
                    const input = e.currentTarget as HTMLInputElement;
                    const start = input.selectionStart ?? 0;
                    const end = input.selectionEnd ?? start;
                    const newStr =
                      addQuantity.slice(0, start) + "," + addQuantity.slice(end);
                    setAddQuantity(newStr);
                    requestAnimationFrame(() =>
                      input.setSelectionRange(start + 1, start + 1)
                    );
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{ width: 110 }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel id="add-ingredient-unit-label" shrink>Unité</InputLabel>
                  <Select
                    labelId="add-ingredient-unit-label"
                    label="Unité"
                    value={addUnit}
                    onChange={(e) => setAddUnit(e.target.value)}
                    displayEmpty
                    sx={{ minWidth: 140 }}
                  >
                    <MenuItem value="">Sans unité</MenuItem>
                    {MEASUREMENT_UNITS.map((u) => (
                      <MenuItem key={u.value} value={u.value}>
                        {u.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  type="button"
                  variant="outline"
                  startIcon={<Add />}
                  onClick={handleAddIngredient}
                  disabled={!addIngredient}
                >
                  Ajouter
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Actions */}
          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}
          >
            <Button
              variant="text"
              onClick={() => navigate("/products")}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={
                isSubmitting ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {isEditing ? "Enregistrer les modifications" : "Créer le produit"}
            </Button>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}
